import React, {useEffect} from "react";
import {useTheme} from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import StepContent from "@mui/material/StepContent";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {grey} from "@mui/material/colors";
import io from "socket.io-client";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {UserContext} from "../../App";

// create socket connection
const socket = io(`${process.env.REACT_APP_BACKEND_URL}`, {
    path: "/socket",
    forceNew: true,
    autoConnect: false,
    transports: ["websocket", "polling", "flashsocket"],
});

    const getStepFinishStatusFromProgress = (progress) => {
    // Define a default array with all steps set to false (not finished)
    const defaultStatus = new Array(5).fill(false);

    if (!progress || typeof progress !== 'object') {
        return defaultStatus;
    }
    const stepToIndexMapping = {
        "imputation_done": [0],
        "const_feature_removal_done": [1],
        "normalisation_done": [2],
        "one_hot_encoding_done": [3],
        "anomalies": [4],
    };

    // Iterate through the progress object using Object.keys
    Object.keys(progress).forEach(stepName => {
        const indices = stepToIndexMapping[stepName];
        if (indices && Array.isArray(indices)) {
            indices.forEach(index => {
                if (typeof index !== 'undefined' && progress[stepName] === true) {
                    defaultStatus[index] = true;
                }
            });
        }
    });

    return defaultStatus;
}

export default function ExperimentProcessingStatus(props) {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();

    // get experiment methods
    const {
        storedUid,
        datasets,
        currentDatasetIdx,
        experiments,
        currentExperimentIdx,
        fetchExperiments,
    } = React.useContext(UserContext);

    // needed for routing to inspection site
    const navigate = useNavigate();
    const steps = [
        {
            label: t("processingStatus.LabelMissingValues"),
            description: t("processingStatus.DescriptionMissingValues"),
        },
        {
            label: t("processingStatus.LabelConstants"),
            description: t("processingStatus.DescriptionConstants"),
        },
        {
            label: t("processingStatus.LabelOneHot"),
            description: t("processingStatus.DescriptionOneHot"),
        },
        {
            label: t("processingStatus.LabelNormalize"),
            description: t("processingStatus.DescriptionNormalize"),
        },
        {
            label: t("processingStatus.LabelAnomalies"),
            description: t("processingStatus.DescriptionAnomalies"),
        },
    ];

    const [stepFinishStatus, setStepFinishStatus] = React.useState([
        false,
        false,
        false,
        false,
        false,
    ]);
    const [activeStep, setActiveStep] = React.useState(0);
    const [isFinished, setIsFinished] = React.useState(false);

    const handleFinish = () => {
        navigate(
            `/inspect/${datasets[currentDatasetIdx].datasetName}/${experiments[currentExperimentIdx].experimentName}`
        );
    };

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };
    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };
    const handleStep = (step) => () => {
        setActiveStep(step);
    };

    /* SOCKET CONNECTION
      The socket.io connection is used, to update the processing progress
    */

const [progressData, setProgressData] = React.useState({
    progress: {completion_percentage: 0, stepnames: []},
    status: "Undefinied",
});

    // connect to socket, when the current dataset is gathered
    useEffect(() => {
        socket.connect();
        console.log("socket not connected")
        socket.on("connect", () => {
             const roomID = `${storedUid}-${datasets[currentDatasetIdx].datasetName}-${props.experimentName}-preprocessing`
            // create room for current user
            socket.emit("create-processing-pipeline-room", roomID);
        });
    }, [datasets, currentDatasetIdx]);

    // these effects will be triggered by the server
    useEffect(() => {
        console.log("here", socket)
        socket.io.on("error", (error) => {
            console.log("socket.io error:", error);
        });

            socket.on("update-processing-pipeline", (data) => {
                                    // Merge the existing state with the received data so that plotting information can be sent seperately
        setProgressData(prevData => {
            const updatedProgressData = {
                ...prevData,
                ...data,
                progress: {
                    ...prevData.progress,
                    ...data.progress
                }
            };

            // Translate progress into finished steps based on updatedProgressData
            const updatedFinishStatus = getStepFinishStatusFromProgress(updatedProgressData.progress);
            setStepFinishStatus(updatedFinishStatus);

            return updatedProgressData;
        });
    });
        socket.on("processing-completed", () => {
            socket.disconnect();
            setIsFinished(true);
            fetchExperiments({latest: props.experimentName});
        });

        // clean up WebSocket connection on component unmount
        return () => {
            socket.disconnect();
        };
    }, []);

    return (
        <Box id="processing-status-container" sx={{padding: theme.spacing(4, 1)}}>
            <Typography variant="h4" gutterBottom>
                {t("processingStatus.PageTitle_1")} {props.experimentName}{" "}
                {t("processingStatus.PageTitle_2")}
            </Typography>
            <Typography variant="body2">
                {t("processingStatus.Description")}
            </Typography>
            <br/>

            <Grid
                container
                spacing={4}
                direction="row"
                justifyContent="flex-start"
                alignItems="start"
            >
                <Grid item lg={6} xs={12}>
                    <LinearProgress
                        variant="determinate"
                        value={progressData.progress.completion_percentage}
                        color="success"
                    />
                    <Typography variant="body2" color="text.secondary">{`${Math.round(
                        progressData.progress.completion_percentage
                    )}%`}</Typography>
                </Grid>
                <Grid lg={6} xs={12}/>
                <Grid lg={6} item xs={12}>
                    <Stepper
                        nonLinear
                        activeStep={activeStep}
                        orientation="vertical"
                        sx={{marginTop: theme.spacing(3)}}
                    >
                    {steps.map((step, index) => (
                        <Step
                            key={step.label}
                            sx={{
                                // overwrite default step colors, so the color is only dependet on whether the status is finished or not
                                "& .MuiStepIcon-root.Mui-active": stepFinishStatus.at(index)
                                    ? {color: theme.palette.success.dark}
                                    : {color: grey[300]},
                                "& .MuiStepIcon-root": stepFinishStatus.at(index)
                                    ? {color: theme.palette.success.main}
                                    : {color: grey[300]},
                            }}
                        >
                            <StepButton onClick={handleStep(index)}>
                                {step.label} {stepFinishStatus.at(index) ? "✅" : ""}
                            </StepButton>
                            <StepContent>
                                <Typography>{step.description}</Typography>
                                <Box sx={{mb: 2}}>
                                    <div>
                                        {
                                            index !== steps.length - 1 ? (
                                                <Button
                                                    variant="contained"
                                                    onClick={handleNext}
                                                    sx={{mt: 1, mr: 1, backgroundColor:'#9b9b9b'}}
                                                >
                                                    {t("processingStatus.ReadNextButton")}
                                                </Button>
                                            ) : (
                                                ""
                                            ) // don't show "Read Next" Button on last step
                                        }
                                        <Button
                                            disabled={index === 0}
                                            onClick={handleBack}
                                            sx={{mt: 1, mr: 1}}
                                        >
                                            {t("processingStatus.BackButton")}
                                        </Button>
                                    </div>
                                </Box>
                            </StepContent>
                        </Step>
                    ))}
                    </Stepper>
                    {isFinished && (
                        <Paper
                            square
                            elevation={0}
                            sx={{p: 3, marginTop: theme.spacing(3), background: "white"}}
                        >
                            <Typography>{t("processingStatus.CompletedMessage")}</Typography>
                            <Button
                                variant="contained"
                                onClick={handleFinish}
                                sx={{mt: 1, mr: 1}}>
                                {t("processingStatus.ProceedButton")}
                            </Button>
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}
